import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { isAxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobalStore } from '@digiwedge/global-state';
import { classifyAxiosError, useLogin, useVerifyLoginMfa } from '@digiwedge/hooks-auth-web';
import { ContentWrap } from '../components/ContentWrap.js';
import { LogoComponent } from '../components/Logo/LogoComponent.js';
import { Heading, PageContent } from '../components/index.js';
import { useSocialAuthCallback, } from '../hooks/useSocialAuthCallback.js';
import { getMessage } from '../i18n/messages.js';
import { useOptionalIdp } from '../providers/IdpProvider.js';
import { decodeExp } from '../providers/auth-boot.js';
import { makeAuthChannel } from '../providers/auth-channel.js';
import { resetRefreshBlock } from '../providers/refresh-guard.js';
import { LoginForm } from './Login/LoginForm.js';
import { MfaPanel } from './Login/MfaPanel.js';
import { OAuthNotLinkedBanner } from './Login/OAuthNotLinkedBanner.js';
import { toast } from '../utils/toast.js';
import { buildAuthUrl, buildLinkUrl, buildSignupUrl, clearSocialLoginPopupFlow, computeLinkReturnBase, computeLoginReturnBase, computeSignupReturnBase, clearPendingSocialState, getProviderLabel, markSocialLoginPopupFlow, notifySocialLoginPopup, persistSocialLoginContext, readSocialLoginPopupFlow, SOCIAL_PENDING_TARGET_KEY, SOCIAL_REMEMBER_KEY, TEE_TIME_WEB_OAUTH_AUDIENCE, parseSocialProviderId, readPendingProvider, readPendingTarget, storePendingProvider, } from '../utils/socialAuth.js';
import { extractLoginMfaChallenge, formatMfaChallengeHint, isLoginSuccessPayload, parseLoginError, } from '../utils/loginResult.js';
import { getPasskeyHttpStatus } from './passkeyRegistration.js';
function redirectTo(url) {
    if (typeof window === 'undefined')
        return;
    if (typeof process !== 'undefined' && process.env?.['JEST_WORKER_ID']) {
        try {
            const testWindow = window;
            testWindow['__DW_TEST_LAST_REDIRECT__'] = url;
        }
        catch {
            /* ignore */
        }
        return;
    }
    try {
        window.location.assign(url);
    }
    catch {
        window.location.href = url;
    }
}
function closePopupWindowSoon() {
    if (typeof window === 'undefined')
        return;
    try {
        window.close();
    }
    catch {
        /* ignore close errors */
    }
}
function broadcastPopupLoginSuccess(payload) {
    try {
        const channel = makeAuthChannel();
        channel.postMessage({
            type: 'REFRESHED',
            accessToken: payload.accessToken,
            refreshToken: payload.refreshToken,
            jti: payload.jti,
            exp: decodeExp(payload.accessToken),
            remember: payload.remember,
        });
        channel.close();
    }
    catch {
        /* ignore broadcast errors */
    }
}
const MFA_INVALID_OR_EXPIRED_MESSAGE = 'Invalid or expired code. Try again or request a new code.';
const MFA_CONTRACT_ERROR_MESSAGE = 'We could not verify this code because the sign-in session changed. Request a new code and try again.';
const MFA_RESEND_COOLDOWN_SECONDS = 60;
const MFA_RESEND_COOLDOWN_MS = MFA_RESEND_COOLDOWN_SECONDS * 1000;
const isChallengeIdValidationMessage = (message) => message.toLowerCase().includes('property challengeid should not exist');
const isCookieAuthOk = (value) => {
    if (!value || typeof value !== 'object')
        return false;
    return value['ok'] === true;
};
const readRetryAfterSeconds = (error) => {
    const data = error.response?.data;
    if (!data || typeof data !== 'object')
        return null;
    const retryAfterSeconds = data.retryAfterSeconds;
    return typeof retryAfterSeconds === 'number' && Number.isFinite(retryAfterSeconds)
        ? Math.max(1, Math.ceil(retryAfterSeconds))
        : null;
};
const getPasskeyLoginMessage = (error) => {
    if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
            return 'Passkey sign-in was cancelled.';
        }
        if (error.name === 'NotSupportedError') {
            return 'This browser cannot use passkeys.';
        }
    }
    const status = getPasskeyHttpStatus(error);
    if (status === 503) {
        return 'Passkey sign-in is not available right now. Use social sign-in or email.';
    }
    if (status === 400 || status === 404) {
        return 'No passkey is available for this account.';
    }
    return 'Unable to sign in with passkey.';
};
function startSocialSignup(provider, requestedReturnTo, remember, policyProfile) {
    persistSocialLoginContext(requestedReturnTo, remember);
    storePendingProvider(provider, requestedReturnTo);
    const url = new URL(buildSignupUrl(provider, { profile: policyProfile }));
    url.searchParams.set('returnTo', computeSignupReturnBase());
    redirectTo(url.toString());
}
export const LoginPage = ({ appName, defaultEmail, defaultRemember, oauthAudience = TEE_TIME_WEB_OAUTH_AUDIENCE, signupPolicyProfile = oauthAudience, placeholderEmail, placeholderPassword, socialLayout = 'stack', showSignup = true, signUpHref, }) => {
    const { login, loading, error } = useLogin();
    const { verifyLoginMfa, loading: verifying } = useVerifyLoginMfa();
    const idpClient = useOptionalIdp();
    const persistLogin = useGlobalStore((state) => state.login);
    const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
    const setAuthenticated = useGlobalStore((state) => state.setAuthenticated);
    const setMfaEnrolled = useGlobalStore((state) => state.setMfaEnrolled);
    const setMfaSessionVerified = useGlobalStore((state) => state.setMfaSessionVerified);
    const navigate = useNavigate();
    const location = useLocation();
    const [oauthNotLinkedProvider, setOauthNotLinkedProvider] = useState(null);
    const queryEmail = useMemo(() => {
        try {
            return new URLSearchParams(location.search).get('email') ?? '';
        }
        catch {
            return '';
        }
    }, [location.search]);
    useEffect(() => {
        // Clear any cached tokens to avoid stale refresh attempts during login
        useGlobalStore.getState().logout();
    }, []);
    const [mfaChallenge, setMfaChallenge] = useState(null);
    const [socialMfaContext, setSocialMfaContext] = useState(null);
    const [mfaError, setMfaError] = useState(null);
    const [rememberDevice, setRememberDevice] = useState(true);
    const [mfaResendAvailableAt, setMfaResendAvailableAt] = useState(null);
    const [mfaCooldownNow, setMfaCooldownNow] = useState(() => Date.now());
    const [resendingMfa, setResendingMfa] = useState(false);
    const [values, setValues] = useState({
        email: defaultEmail ?? queryEmail,
        password: '',
        remember: Boolean(defaultRemember),
    });
    const [emailTouched, setEmailTouched] = useState(false);
    const [passkeyAuthenticating, setPasskeyAuthenticating] = useState(false);
    const { messages: loginErrorMessages, statusCode: loginErrorStatusCode } = useMemo(() => parseLoginError(error), [error]);
    const loginErrorGuidance = useMemo(() => {
        if (loginErrorStatusCode === 401) {
            return 'Double-check your email and password. If you need help, contact your administrator.';
        }
        return undefined;
    }, [loginErrorStatusCode]);
    const currentReturnTo = useMemo(() => {
        try {
            const params = new URLSearchParams(location.search);
            const candidate = params.get('returnTo');
            return candidate && candidate.startsWith('/') ? candidate : null;
        }
        catch {
            return null;
        }
    }, [location.search]);
    const forgotPasswordHref = useMemo(() => {
        const params = new URLSearchParams();
        const trimmedEmail = values.email.trim();
        if (trimmedEmail) {
            params.set('email', trimmedEmail);
        }
        if (currentReturnTo) {
            params.set('returnTo', currentReturnTo);
        }
        const query = params.toString();
        return query ? `/forgot-password?${query}` : '/forgot-password';
    }, [values.email, currentReturnTo]);
    const inMfaMode = Boolean(mfaChallenge);
    const mfaHint = formatMfaChallengeHint(mfaChallenge);
    const resendCooldownSeconds = useMemo(() => {
        if (!mfaChallenge || !mfaResendAvailableAt)
            return 0;
        return Math.max(0, Math.ceil((mfaResendAvailableAt - mfaCooldownNow) / 1000));
    }, [mfaChallenge, mfaCooldownNow, mfaResendAvailableAt]);
    const passkeySupported = browserSupportsWebAuthn();
    const passkeyAvailable = Boolean(idpClient && passkeySupported);
    const passkeyDisabledReason = !idpClient
        ? 'Passkey sign-in is unavailable in this login shell.'
        : !passkeySupported
            ? 'Passkeys are not available on this browser or device.'
            : undefined;
    const showMfaChallenge = useCallback((challenge) => {
        const now = Date.now();
        setMfaChallenge(challenge);
        setMfaError(null);
        setMfaCooldownNow(now);
        setMfaResendAvailableAt(now + MFA_RESEND_COOLDOWN_MS);
    }, []);
    useEffect(() => {
        if (!mfaChallenge || !mfaResendAvailableAt || resendCooldownSeconds <= 0)
            return;
        const timer = window.setInterval(() => {
            setMfaCooldownNow(Date.now());
        }, 1000);
        return () => window.clearInterval(timer);
    }, [mfaChallenge, mfaResendAvailableAt, resendCooldownSeconds]);
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setValues((v) => ({
            ...v,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };
    const startSocialLogin = (provider) => {
        const params = new URLSearchParams(window.location.search);
        const requestedReturnTo = params.get('returnTo');
        persistSocialLoginContext(requestedReturnTo, values.remember);
        storePendingProvider(provider, requestedReturnTo);
        const authUrl = new URL(buildAuthUrl(provider, { audience: oauthAudience }));
        authUrl.searchParams.set('returnTo', computeLoginReturnBase());
        redirectTo(authUrl.toString());
    };
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('oauthPopup') !== '1') {
            return;
        }
        const provider = parseSocialProviderId(params.get('provider'));
        const flowId = params.get('flowId')?.trim() ?? '';
        if (!provider || !flowId) {
            notifySocialLoginPopup({
                flowId: flowId || 'invalid-popup-flow',
                status: 'complete',
                ok: false,
                provider: null,
                error: provider ? 'Missing social sign-in flow id.' : 'Unsupported social provider.',
            });
            closePopupWindowSoon();
            return;
        }
        const requestedReturnTo = params.get('returnTo');
        const safeReturnTo = requestedReturnTo?.startsWith('/') ? requestedReturnTo : null;
        const remember = defaultRemember ?? true;
        const popupMode = params.get('mode') === 'signup' ? 'signup' : 'login';
        persistSocialLoginContext(safeReturnTo, remember);
        storePendingProvider(provider, safeReturnTo);
        markSocialLoginPopupFlow(provider, flowId);
        notifySocialLoginPopup({
            flowId,
            status: 'started',
            ok: true,
            provider,
            mode: popupMode,
        });
        const authUrl = popupMode === 'signup'
            ? new URL(buildSignupUrl(provider, { profile: signupPolicyProfile }))
            : new URL(buildAuthUrl(provider, { audience: oauthAudience }));
        authUrl.searchParams.set('returnTo', popupMode === 'signup'
            ? `${window.location.origin}/signup?oauthPopup=1&provider=${provider}&flowId=${encodeURIComponent(flowId)}`
            : computeLoginReturnBase());
        redirectTo(authUrl.toString());
    }, [defaultRemember, location.search, oauthAudience, signupPolicyProfile]);
    const navigateAfterLogin = useCallback((rememberFlag) => {
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get('returnTo');
        const target = returnTo && returnTo.startsWith('/') ? returnTo : '/';
        const pendingProvider = readPendingProvider();
        if (pendingProvider) {
            const linkUrl = new URL(buildLinkUrl(pendingProvider));
            linkUrl.searchParams.set('returnTo', computeLinkReturnBase());
            try {
                sessionStorage.setItem(SOCIAL_REMEMBER_KEY, rememberFlag ? 'true' : 'false');
                const storedTarget = readPendingTarget() ?? target;
                sessionStorage.setItem(SOCIAL_PENDING_TARGET_KEY, storedTarget);
            }
            catch {
                /* ignore */
            }
            toast.info(`Linking ${getProviderLabel(pendingProvider)}...`);
            redirectTo(linkUrl.toString());
            return;
        }
        navigate(target);
    }, [navigate]);
    const handlePasskeyLogin = useCallback(async () => {
        if (!idpClient) {
            toast.error('Passkey sign-in is unavailable in this login shell.');
            return;
        }
        if (!passkeySupported) {
            toast.error('Passkeys are not available on this browser or device.');
            return;
        }
        setPasskeyAuthenticating(true);
        try {
            const email = values.email.trim();
            const data = await idpClient.beginPasskeyAuthentication(email ? { email } : {});
            const optionsJSON = data.optionsJSON ?? data.options;
            if (!optionsJSON) {
                throw new Error('Passkey authentication options missing from server response.');
            }
            const assertion = await startAuthentication({
                optionsJSON: optionsJSON,
            });
            const result = await idpClient.verifyPasskeyAuthentication({
                challengeId: data.challengeId,
                response: assertion,
            });
            const challenge = extractLoginMfaChallenge(result, result.userId);
            if (challenge) {
                showMfaChallenge(challenge);
                toast.success(getMessage('en-US', 'mfaInitiatedSuccess'));
                return;
            }
            if (isLoginSuccessPayload(result)) {
                persistLogin(result.accessToken, result.refreshToken, result.jti, values.remember);
            }
            else if (idpClient.tokenDelivery === 'cookie' && isCookieAuthOk(result)) {
                setAuthenticated();
            }
            else {
                throw new Error('Passkey authentication did not return a login session.');
            }
            resetRefreshBlock();
            toast.success(getMessage('en-US', 'loginSuccess'));
            navigateAfterLogin(values.remember);
        }
        catch (err) {
            toast.error(getPasskeyLoginMessage(err));
        }
        finally {
            setPasskeyAuthenticating(false);
        }
    }, [
        idpClient,
        navigateAfterLogin,
        passkeySupported,
        persistLogin,
        setAuthenticated,
        showMfaChallenge,
        values.email,
        values.remember,
    ]);
    const onSubmit = async (e) => {
        e.preventDefault();
        setMfaError(null);
        try {
            const result = await login(values);
            if (isLoginSuccessPayload(result) && result.mfaRequired !== true) {
                toast.success(getMessage('en-US', 'loginSuccess'));
                setMfaChallenge(null);
                navigateAfterLogin(values.remember);
                return;
            }
            const challenge = extractLoginMfaChallenge(result, result.userId);
            if (challenge) {
                showMfaChallenge(challenge);
                toast.success(getMessage('en-US', 'mfaInitiatedSuccess'));
                return;
            }
            if (isLoginSuccessPayload(result)) {
                toast.success(getMessage('en-US', 'loginSuccess'));
                setMfaChallenge(null);
                navigateAfterLogin(values.remember);
                return;
            }
            toast.success(getMessage('en-US', 'loginSuccess'));
            setMfaChallenge(null);
            navigateAfterLogin(values.remember);
        }
        catch (err) {
            const { messages } = parseLoginError(err);
            toast.error(messages[0] ?? getMessage('en-US', 'loginError'));
        }
    };
    const handleResendMfa = async () => {
        if (resendCooldownSeconds > 0)
            return;
        setMfaError(null);
        setResendingMfa(true);
        try {
            const result = await login(values);
            const challenge = extractLoginMfaChallenge(result, result.userId);
            if (challenge) {
                showMfaChallenge(challenge);
                toast.success(getMessage('en-US', 'mfaInitiatedSuccess'));
                return;
            }
            if (isLoginSuccessPayload(result)) {
                toast.success(getMessage('en-US', 'loginSuccess'));
                setMfaChallenge(null);
                navigateAfterLogin(values.remember);
                return;
            }
            setMfaError('Unable to send a new code. Go back to login and try again.');
        }
        catch (err) {
            const { messages } = parseLoginError(err);
            setMfaError(messages[0] ?? getMessage('en-US', 'loginError'));
        }
        finally {
            setResendingMfa(false);
        }
    };
    const handleVerifyMfa = async (code) => {
        setMfaError(null);
        if (!mfaChallenge) {
            const message = getMessage('en-US', 'loginMfaSessionExpired');
            setMfaError(message);
            toast.error(message);
            return;
        }
        if (!mfaChallenge.userId) {
            const message = getMessage('en-US', 'loginMfaMissingUser');
            setMfaError(message);
            toast.error(message);
            setMfaChallenge(null);
            return;
        }
        try {
            await verifyLoginMfa({
                userId: mfaChallenge.userId,
                challengeId: mfaChallenge.challengeId,
                code,
                remember: socialMfaContext?.remember ?? values.remember,
                rememberDevice,
            });
            setMfaEnrolled(true);
            setMfaSessionVerified(true);
            toast.success(getMessage('en-US', 'loginSuccess'));
            setMfaChallenge(null);
            setMfaResendAvailableAt(null);
            if (socialMfaContext) {
                const popupProvider = readSocialLoginPopupFlow();
                clearPendingSocialState();
                if (popupProvider) {
                    notifySocialLoginPopup({
                        flowId: popupProvider.flowId,
                        status: 'complete',
                        ok: true,
                        provider: popupProvider.provider,
                        target: socialMfaContext.target,
                    });
                    clearSocialLoginPopupFlow();
                    closePopupWindowSoon();
                    setSocialMfaContext(null);
                    return;
                }
                navigate(socialMfaContext.target);
                setSocialMfaContext(null);
                return;
            }
            navigateAfterLogin(values.remember);
        }
        catch (err) {
            if (isAxiosError(err)) {
                const { code, message } = classifyAxiosError(err, getMessage('en-US', 'loginError'));
                if (code === 'otp-invalid') {
                    setMfaError(MFA_INVALID_OR_EXPIRED_MESSAGE);
                    toast.error(MFA_INVALID_OR_EXPIRED_MESSAGE);
                }
                else if (code === 'otp-expired') {
                    const errorMessage = 'Code expired. Request a new code.';
                    setMfaError(errorMessage);
                    toast.error(errorMessage);
                }
                else if (code === 'otp-rate-limited') {
                    const retryAfterSeconds = readRetryAfterSeconds(err);
                    const errorMessage = retryAfterSeconds
                        ? `Too many attempts. Try again in ${retryAfterSeconds} seconds.`
                        : 'Too many attempts. Please wait and try again.';
                    setMfaError(errorMessage);
                    toast.error(errorMessage);
                }
                else if (code === 'server-error' || code === 'network-error') {
                    const errorMessage = `${message}. You can try again.`;
                    setMfaError(errorMessage);
                    toast.error(errorMessage);
                }
                else if (isChallengeIdValidationMessage(message)) {
                    setMfaError(MFA_CONTRACT_ERROR_MESSAGE);
                    toast.error(MFA_CONTRACT_ERROR_MESSAGE);
                }
                else {
                    setMfaError(message);
                    toast.error(message);
                }
            }
            else {
                const message = getMessage('en-US', 'loginError');
                setMfaError(message);
                toast.error(message);
            }
        }
    };
    const handleCancelMfa = () => {
        setMfaError(null);
        setMfaChallenge(null);
        setSocialMfaContext(null);
        setMfaResendAvailableAt(null);
    };
    const handleSocialLoginSuccess = useCallback(({ accessToken, refreshToken, jti, remember, target }) => {
        const payload = { accessToken, refreshToken, jti, remember, target };
        useGlobalStore.getState().login(accessToken, refreshToken, jti, remember);
        const popupProvider = readSocialLoginPopupFlow();
        if (popupProvider) {
            broadcastPopupLoginSuccess(payload);
            notifySocialLoginPopup({
                flowId: popupProvider.flowId,
                status: 'complete',
                ok: true,
                provider: popupProvider.provider,
                target,
            });
            clearSocialLoginPopupFlow();
            closePopupWindowSoon();
            return;
        }
        navigate(target);
    }, [navigate]);
    const handleSocialMfaRequired = useCallback((payload) => {
        setSocialMfaContext(payload);
        showMfaChallenge(payload.challenge);
    }, [showMfaChallenge]);
    useEffect(() => {
        const popupProvider = readSocialLoginPopupFlow();
        if (!popupProvider || !isAuthenticated) {
            return;
        }
        notifySocialLoginPopup({
            flowId: popupProvider.flowId,
            status: 'complete',
            ok: true,
            provider: popupProvider.provider,
        });
        clearSocialLoginPopupFlow();
        closePopupWindowSoon();
    }, [isAuthenticated]);
    useSocialAuthCallback({
        defaultRemember: defaultRemember ?? true,
        onLoginSuccess: handleSocialLoginSuccess,
        onNotLinked: setOauthNotLinkedProvider,
        onMfaRequired: handleSocialMfaRequired,
    });
    return (_jsx(PageContent, { children: _jsx(ContentWrap, { className: "flex items-center justify-center md:justify-end py-8 min-h-screen", children: _jsxs("div", { className: "w-full max-w-md bg-white px-4 sm:px-8 py-8 rounded-lg shadow-lg border border-gray-100 flex flex-col items-center", children: [_jsx("div", { className: "mb-6 text-center", children: _jsx(LogoComponent, { themeMode: "light", size: "large" }) }), _jsx(Heading, { variant: "h3", className: "text-xl sm:text-2xl font-semibold text-center mb-6", children: inMfaMode
                            ? getMessage('en-US', 'mfaCodeHeading')
                            : getMessage('en-US', 'loginWelcome').replace('{appName}', String(appName)) }), inMfaMode ? (_jsx(MfaPanel, { error: mfaError, hint: mfaHint, onVerify: handleVerifyMfa, onResend: handleResendMfa, resendCooldownSeconds: resendCooldownSeconds, resending: resendingMfa, verifying: verifying, onCancel: handleCancelMfa, rememberDevice: rememberDevice, onRememberDeviceChange: setRememberDevice })) : (_jsxs(_Fragment, { children: [oauthNotLinkedProvider && (_jsx(OAuthNotLinkedBanner, { provider: oauthNotLinkedProvider, onDismiss: () => setOauthNotLinkedProvider(null), onSignup: (provider) => {
                                    const params = new URLSearchParams(window.location.search);
                                    const requestedReturnTo = params.get('returnTo');
                                    startSocialSignup(provider, requestedReturnTo, values.remember, signupPolicyProfile);
                                } })), _jsx(LoginForm, { values: values, loading: loading, placeholderEmail: placeholderEmail, placeholderPassword: placeholderPassword, forgotPasswordHref: forgotPasswordHref, socialLayout: socialLayout, showSignup: showSignup, signUpHref: signUpHref, loginErrorMessages: loginErrorMessages, loginErrorGuidance: loginErrorGuidance, hasError: Boolean(error), emailTouched: emailTouched, onEmailBlur: () => setEmailTouched(true), onChange: handleChange, onSubmit: onSubmit, onSocialLogin: startSocialLogin, onPasskeyLogin: handlePasskeyLogin, passkeyAvailable: passkeyAvailable, passkeyLoading: passkeyAuthenticating, passkeyDisabledReason: passkeyDisabledReason })] }))] }) }) }));
};
//# sourceMappingURL=LoginPage.js.map